import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { TemplateBuilderPreviewQuestion } from "pages/checks/templates/builder/preview/questions";
import { TemplateBuilderPreviewSection } from "pages/checks/templates/builder/preview/section/TemplateBuilderPreviewSection";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import './TemplateBuilderPreview.css';
import { VisionAppDisplay } from "components/app";
import { CheckCompletionStatsHelper } from "pages/checks/templates/builder/preview/CheckCompletionStatsHelper";
import { CheckConditionalityHelper } from "pages/checks/templates/builder/preview/CheckConditionalityHelper";
import classNames from "classnames";
import { Intent } from "@blueprintjs/core";
import { HelperFunctions } from "helpers";

const defaultObject = {};

export function TemplateBuilderPreview(
    {
        templateOptions = defaultObject,
        layout,
        mediaMap,
        onClose,
        show,
        template
    }
) {
    const [currentSection, setCurrentSection] = useState(null);
    const [allAnswers, setAllAnswers] = useState([]);
    const [path, setPath] = useState([]);
    const [category, setCategory] = useState({
        name: ""
    });

    const [canCompleteSlide, setCanCompleteSlide] = useState(false);

    const currentViewIndex = useMemo(() => {
        if (path.length) {
            return path[path.length - 1].viewIndex ?? 0;
        }

        return 0;
    }, [path]);

    const setCurrentViewIndex = (viewIndex) => {
        if (path.length) {
            const lastPath = path[path.length - 1];
            const newLastPath = { ...lastPath, viewIndex: viewIndex };
            const pathClone = [...path];
            pathClone[path.length - 1] = newLastPath;
            setPath(pathClone);
        }
    }

    const preventSlideAnimation = useRef(false);

    const getValidAnswersForCurrentSection = (answers) => {
        const repeatableAnswerGroupIds = path.filter(p => p.repeatableAnswerGroupId)
            .map(p => p.repeatableAnswerGroupId);

        let result = answers;

        for (const repeatableAnswerGroupId of repeatableAnswerGroupIds) {
            result = result.find(i => i.repeatableAnswerGroupId === repeatableAnswerGroupId)
                .answers;
        }

        return result;
    }

    const getRelevantAnswersForCurrentSection = (answers) => {
        const repeatableAnswerGroupIds = path.filter(p => p.repeatableAnswerGroupId)
            .map(p => p.repeatableAnswerGroupId);

        let result = answers;

        for (const repeatableAnswerGroupId of repeatableAnswerGroupIds) {
            const childAnswers = result.find(i => i.repeatableAnswerGroupId === repeatableAnswerGroupId)
                .answers;

            // Filter out repeatable answers. We only want repeatable answers for the last
            // section in the path.
            result = [...result.filter(a => a.$answerType !== "Repeatable"), ...childAnswers]
        }

        return result;
    }

    const answersForSection = useMemo(() => getRelevantAnswersForCurrentSection(allAnswers), [allAnswers, currentSection, layout]);

    useEffect(() => {
        if (templateOptions && template) {
            const newCategory = templateOptions.templateCategories
                .find(c => c.subCategories.some(s => s.key === template.subCategory));

            setCategory(newCategory);
        }
    }, [templateOptions, template]);

    const reset = () => {
        setPath([]);
        setAllAnswers([]);
    }

    useEffect(reset, [layout]);

    useEffect(() => {
        if (path.length > 0) {
            setCurrentSection(path[path.length - 1].section);
        } else {
            setCurrentSection(null);
        }
    }, [path]);

    const isSectionOrPage = (component) => component.$componentType === 'StandardContainer' || !component.$componentType;

    const getVisibleComponentsWithinComponent = (component) => {
        if (!CheckConditionalityHelper.shouldComponentShow(component, answersForSection, childComponents)) {
            return [];
        }

        if (isSectionOrPage(component)) {
            if (component.title) {
                return getVisibleComponentsWithinSection(component);
            }
            else {
                return component.childComponents
                    .map(c => getVisibleComponentsWithinComponent(c))
                    .flat();
            }
        } else {
            return component;
        }
    }

    const findAllRepeatableAnswersForSection = (section) => {
        return answersForSection
            .filter(a => a.$answerType === "Repeatable" && a.containerId === section.id);
    }

    const getVisibleComponentsWithinSection = (section) => {
        const result = [
            { ...section, repeatCount: section.repeatability != null ? 0 : null }
        ];

        if (section.repeatability != null) {
            const answersInScope = findAllRepeatableAnswersForSection(section);

            answersInScope.forEach((a, i) => {
                if (i > 0) {
                    result.push({ ...section, repeatCount: i });
                }
            })
        }

        return result;
    }

    const findRepeatableAnswersForQuestion = (question, answers) => {
        const answersInScope = getValidAnswersForCurrentSection(answers);

        return answersInScope
            .find(a => a.$answerType === "Repeatable" && a.questionId === question.id);
    }

    const calculateChildComponents = (section) => section ? section.childComponents : layout.pages;
    const childComponents = useMemo(() => calculateChildComponents(currentSection), [currentSection, layout]);

    const calculateVisibleSlides = (allVisibleComponents) => {
        const willShowQuestionView = allVisibleComponents.some(x => !isSectionOrPage(x));
        let newVisibleSlides = [];

        if (willShowQuestionView) {
            let currentSections = [];

            for (let i = 0; i < allVisibleComponents.length; i++) {
                const component = allVisibleComponents[i];

                if (isSectionOrPage(component)) {
                    currentSections.push(component);
                } else if (currentSections.length) {
                    newVisibleSlides.push({ components: currentSections, key: `multiple-component-${currentSections.map(x => x.id).join(",")}` });
                    currentSections = [];
                }

                if (!isSectionOrPage(component)) {
                    newVisibleSlides.push({ components: [component], key: `single-component-${component.id}-type-${component.$componentType}` })
                }
            }

            if (currentSections.length) {
                newVisibleSlides.push({ components: currentSections, key: `multiple-component-${currentSections.map(x => x.id).join(",")}` });
            }

            newVisibleSlides.push({ components: [], key: 'complete', finalSlide: true })
        } else {
            newVisibleSlides.push({ components: allVisibleComponents, key: "single-slide", completeAtEnd: true })
        }

        return newVisibleSlides;
    };

    const visibleComponents = useMemo(() => childComponents.map(x => getVisibleComponentsWithinComponent(x)).flat(), [childComponents, answersForSection, layout]);
    const visibleSlides = useMemo(() => calculateVisibleSlides(visibleComponents), [visibleComponents]);

    const showQuestionView = useMemo(() => visibleComponents.some(c => !isSectionOrPage(c)), [visibleComponents]);

    const currentVisibleSlide = useMemo(() => visibleSlides[currentViewIndex], [currentViewIndex, visibleSlides]);

    useEffect(() => {
        if (currentVisibleSlide) {
            const completionStats = CheckCompletionStatsHelper.getCompletionStatsForSectionsAndQuestions(currentVisibleSlide?.components, answersForSection);
            setCanCompleteSlide(completionStats.answeredCount === completionStats.count);
        }
    }, [allAnswers, currentVisibleSlide, layout]);

    const addEmptyAnswerForCurrentSlideIfNotExists = () => {
        if (!currentVisibleSlide?.components?.length) {
            return;
        }

        if (isSectionOrPage(currentVisibleSlide.components[0])) {
            for (const section of currentVisibleSlide.components) {
                if (!section.repeatability) {
                    continue;
                }

                const repeatableAnswers = findAllRepeatableAnswersForSection(section);

                if (!repeatableAnswers.length) {
                    addRepeatableSection(section);
                }
            }
        }
        else {
            // If component is question, only one is displayed on slide at a time.
            const question = currentVisibleSlide.components[0];

            if (question.repeatability) {
                const repeatableAnswers = findRepeatableAnswersForQuestion(question, allAnswers);

                if (!repeatableAnswers) {
                    addRepeatableQuestion(question);
                }
            } else if (!answersForSection.some(x => x.questionId === question.id)) {
                onAnswerChange(question, {
                    $answerType: getAnswerType(question.$componentType),
                    questionId: question.id
                });
            }
        }
    }

    useEffect(() => {
        addEmptyAnswerForCurrentSlideIfNotExists();
    }, [currentVisibleSlide]);

    const scrollTo = useRef();

    const scrollToCurrentSlide = () => {
        if (preventSlideAnimation.current) {
            setTimeout(() => {
                preventSlideAnimation.current = false;
            }, 100)
        }
        setTimeout(() => {
            if (scrollTo.current) {
                scrollTo.current.scrollIntoView({ behavior: preventSlideAnimation.current ? 'instant' : 'smooth', block: 'center', inline: 'center' });
            }
        }, 0);
    }

    useEffect(() => {
        scrollToCurrentSlide();
    }, [currentVisibleSlide, visibleSlides, show]);

    useEffect(() => {
        if (showQuestionView) {
            window.addEventListener('resize', scrollToCurrentSlide)
        }
    }, [showQuestionView])


    const navigateToSection = (section, repeatableAnswerGroupId) => {
        preventSlideAnimation.current = true;
        const pathClone = [...path];
        pathClone.push({
            section: section,
            repeatableAnswerGroupId: repeatableAnswerGroupId
        });

        setPath(pathClone)
    }

    const renderSlide = (slide) => {
        return <div
            key={slide.key}
            ref={currentVisibleSlide === slide ? scrollTo : null}
            className={classNames("check-component-container")}
        >
            {slide.components.map(x => renderComponent(x))}
            <ShowHide
                evaluator={!!slide.finalSlide}
                show={renderCompleteSection()}
            />
            <ShowHide
                evaluator={!!slide.completeAtEnd}
                show={renderCompleteButton()}
            />
        </div>
    };

    const renderComponent = (component) => {
        if (isSectionOrPage(component)) {
            return renderSection(component);
        } else {
            return renderQuestion(component);
        }
    }

    const renderQuestion = (question) => {

        const helperImagesUrls = (question.helperImages || [])
            .map((mediaReference) => mediaMap[mediaReference])
            .filter(Boolean);

        if (question.repeatability != null) {
            const repeatableAnswerGroup = findRepeatableAnswersForQuestion(question, allAnswers);
            const repeatedAnswers = repeatableAnswerGroup ? repeatableAnswerGroup.answers : [];

            return <TemplateBuilderPreviewQuestion
                key={`q-${question.id}-r-${question.repeatCount}`}
                question={question}
                answersInScope={repeatedAnswers}
                onChange={(newAnswers) => onAnswerChangeWithinRepeatableQuestion(question, newAnswers)}
                onAddMore={() => addRepeatableQuestion(question)}
                metaData={`${currentViewIndex + 1} of ${visibleSlides.length}`}
                helperImages={helperImagesUrls}
            />
        } else {
            return <TemplateBuilderPreviewQuestion
                key={`q-${question.id}`}
                question={question}
                answersInScope={answersForSection}
                onChange={(newAnswer) => onAnswerChange(question, newAnswer)}
                metaData={`${currentViewIndex + 1} of ${visibleSlides.length - 1}`}
                helperImages={helperImagesUrls}

            />
        }
    }

    const renderSection = (section) => {
        if (section.repeatability != null) {
            const answersInScope = findAllRepeatableAnswersForSection(section);
            const answersForThisSectionRepeat = answersInScope.length > section.repeatCount ? answersInScope[section.repeatCount] : { answers: [] };
            const allRelevantAnswers = [...answersForSection.filter(x => x.$answerType !== "Repeatable"), ...answersForThisSectionRepeat.answers];

            return <TemplateBuilderPreviewSection
                key={`q-${section.id}-r-${section.repeatCount}`}
                section={section}
                answersForSection={allRelevantAnswers}
                onClick={() => navigateToSection(section, answersForThisSectionRepeat.repeatableAnswerGroupId)}
                canDelete={answersInScope.length > 1}
                canRepeat={answersInScope.length - 1 < section.repeatability.maximumNumberOfRepeats}
                onRepeat={() => addRepeatableSection(section)}
                onDelete={() => clearRepeatableSection(answersForThisSectionRepeat)}
                warningMessage={answersInScope.length - 1 < section.repeatability.minimumNumberOfRepeats ? "More sections are required" : ""}
            />
        } else {
            return <TemplateBuilderPreviewSection
                key={`q-${section.id}`}
                section={section}
                answersForSection={answersForSection}
                onClick={() => navigateToSection(section)}
            />
        }
    }

    const addRepeatableSection = (section) => {
        const answersClone = [...allAnswers];

        const answersInScope = getValidAnswersForCurrentSection(answersClone);

        answersInScope.push({
            $answerType: "Repeatable",
            answers: [],
            containerId: section.id,
            repeatableAnswerGroupId: crypto.randomUUID()
        });

        setAllAnswers(answersClone);
    }

    const addRepeatableQuestion = (question) => {
        const answersClone = [
            ...allAnswers
        ];

        const repeatableAnswers = findRepeatableAnswersForQuestion(question, answersClone);
        const newAnswer = {
            $answerType: getAnswerType(question.$componentType),
            answer: null,
            questionId: question.id
        };

        if (repeatableAnswers) {
            repeatableAnswers.answers.push(newAnswer);
        } else {
            const answersInScope = getValidAnswersForCurrentSection(answersClone);
            answersInScope.push({
                $answerType: "Repeatable",
                answers: [newAnswer],
                questionId: question.id
            })
        }

        setAllAnswers(answersClone);
    }

    const getAnswerType = (questionType) => {
        return templateOptions.questionTypes.find(q => `${q.key}Question` === questionType)
            .answerType;
    }

    const clearRepeatableSection = (repeatableAnswers) => {
        const answersClone = [...allAnswers];

        const answersInScope = getValidAnswersForCurrentSection(answersClone);
        const indexOfRepeatableAnswers = answersInScope.findIndex(a => a.repeatableAnswerGroupId === repeatableAnswers.repeatableAnswerGroupId);
        answersInScope.splice(indexOfRepeatableAnswers, 1);

        setAllAnswers(answersClone);
    }

    const onAnswerChange = (question, answer) => {
        const answersClone = HelperFunctions.deepClone(allAnswers);

        const answers = getValidAnswersForCurrentSection(answersClone);

        const existingAnswerIndex = answers.findIndex(a => a.questionId === question.id);
        if (existingAnswerIndex < 0) {
            answers.push(answer);
        } else if (!answer) {
            answers.splice(existingAnswerIndex, 1);
        } else {
            answers[existingAnswerIndex] = answer;
        }

        setAllAnswers(answersClone);
    }

    const onAnswerChangeWithinRepeatableQuestion = (question, answers) => {
        const answersClone = [
            ...allAnswers
        ];

        const repeatableAnswers = findRepeatableAnswersForQuestion(question, answersClone);
        repeatableAnswers.answers = [...answers];
        setAllAnswers(answersClone);
    }

    const goBack = () => {
        preventSlideAnimation.current = true;
        const pathClone = [...path];
        pathClone.splice(pathClone.length - 1, 1);

        setPath(pathClone);
    }

    const renderDummyEntityInformation = () => {
        if (category?.key === 'Asset') {
            return "AB12 CDE · Heavy Goods Vehicle · Glasgow"
        }

        return "";
    }

    const renderCompleteSection = () => {
        return <div className="check-preview-section">
            <div className="check-preview-section-metadata">
                {currentSection?.title} complete
            </div>
            <div className="check-preview-section-title">
                <h4>All questions within this section have been completed</h4>
            </div>
            <hr></hr>
            <div className="check-preview-section-buttons">
                {renderCompleteButton()}
            </div>
        </div>
    }

    const renderCompleteButton = () => {
        return <Button
            onClick={currentSection ? goBack : complete}
            className="check-continue-button"
            intent={Intent.PRIMARY}
            disabled={!canCompleteSlide}
            text={currentSection ? "Continue" : "Complete"}
        />
    }

    const complete = () => {
        onCloseValue();
    }

    const onCloseValue = () => {
        reset();
        onClose();
    }

    return (
        <VisionAppDisplay
            show={show}
            onClose={onClose}
            header={<div className="check-preview-title">
                <h2>{currentSection ? currentSection.title : template.name}</h2>
                <span>{renderDummyEntityInformation()}</span>
            </div>}
        >
            <div className="check-secondary-header">
                <div>
                    <ShowHide
                        evaluator={!!currentSection}
                        show={<Button
                            onClick={goBack}
                            text={currentSection?.title}
                            icon="chevron-left"
                            minimal={true}
                        />}
                    />
                </div>
            </div>
            <div className={classNames("check-preview", { "check-scrollable-view": showQuestionView })}>
                {visibleSlides.map(c => renderSlide(c))}
            </div>
            <ShowHide
                evaluator={showQuestionView}
                show={<div className="check-footer">
                    <Button
                        onClick={() => setCurrentViewIndex(currentViewIndex - 1)}
                        disabled={currentViewIndex <= 0}
                        text="Prev"
                        icon="chevron-left"
                        minimal={true}
                    />

                    <ShowHide
                        evaluator={currentViewIndex === visibleSlides.length - 1}
                        show={<span>Complete</span>}
                        hide={<span>Check {currentViewIndex + 1} of {visibleSlides.length - 1}</span>}
                    />

                    <Button
                        onClick={() => setCurrentViewIndex(currentViewIndex + 1)}
                        disabled={currentViewIndex >= visibleSlides.length - 1 || !canCompleteSlide}
                        text="Next"
                        rightIcon="chevron-right"
                        minimal={true}
                    />
                </div>}
            />
        </VisionAppDisplay>
    );
}

TemplateBuilderPreview.propTypes = {
    layout: PropTypes.object.isRequired,
    template: PropTypes.object.isRequired,
    templateOptions: PropTypes.object,
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    mediaMap: PropTypes.object.isRequired
};